// "+ New candidate" on the Candidates tab (hub, 2026-10-06). The form, and nothing else: every rule -- validation, the
// duplicate check, the two rows, consent -- lives in lib/newCandidate.js, so the tests run it against a fake client.
import { useState } from "react";
import { EMPTY_NEW_CANDIDATE, NEW_CANDIDATE_DISCIPLINES, createManualCandidate, validateNewCandidate } from "./lib/newCandidate";
import { STATE_CODES } from "./lib/leadState";

const label = { fontSize: 11, color: "#64748b", fontWeight: 600 };
const errStyle = { fontSize: 11, color: "#b91c1c", marginTop: 3 };

export function NewCandidateButton({ onOpen }) {
  return (
    <button className="nb" data-testid="new-candidate-open" onClick={onOpen}
      style={{ background: "#1d4ed8", color: "white", borderRadius: 8, padding: "7px 16px", fontSize: 12, fontWeight: 600 }}>
      ＋ New candidate
    </button>
  );
}

/** client: the supabase client. onCreated({ lead, candidate }) after both rows are written. */
export function NewCandidateModal({ client, onCreated, onClose, now = () => new Date() }) {
  const [form, setForm] = useState(EMPTY_NEW_CANDIDATE);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const errors = validateNewCandidate(form);
  const show = (k) => touched && errors[k] ? <div style={errStyle} data-testid={`new-candidate-error-${k}`}>{errors[k]}</div> : null;

  const save = async () => {
    setTouched(true);
    setFailure(null);
    if (Object.keys(errors).length) return;
    setBusy(true);
    try {
      const out = await createManualCandidate(client, form, now());
      onCreated(out);
    } catch (e) {
      setFailure(e.message || "Could not create the candidate");
    } finally {
      setBusy(false);
    }
  };

  const input = (k, l, props = {}) => (
    <div>
      <label style={label} htmlFor={`nc-${k}`}>{l}</label>
      <input id={`nc-${k}`} data-testid={`new-candidate-${k}`} className="input" value={form[k]} disabled={busy}
        onChange={(e) => set(k, e.target.value)} style={{ marginTop: 4 }} {...props} />
      {show(k)}
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0008", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1001 }} onClick={onClose}>
      <div className="card" data-testid="new-candidate-modal" style={{ width: 520, maxHeight: "85vh", overflow: "auto", padding: 24 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>New candidate</h3>
        <p style={{ fontSize: 12, color: "#64748b", marginBottom: 16 }}>Creates the lead and the candidate (stage New Lead, source manual). The agent texts them only if SMS consent is checked.</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {input("firstName", "First name")}
          {input("lastName", "Last name")}
          <div>
            <label style={label} htmlFor="nc-discipline">Discipline</label>
            <select id="nc-discipline" data-testid="new-candidate-discipline" value={form.discipline} disabled={busy}
              onChange={(e) => set("discipline", e.target.value)} style={{ marginTop: 4, width: "100%" }}>
              {NEW_CANDIDATE_DISCIPLINES.map((d) => <option key={d}>{d}</option>)}
            </select>
            {show("discipline")}
          </div>
          <div>
            <label style={label} htmlFor="nc-state">State</label>
            <select id="nc-state" data-testid="new-candidate-state" value={form.state} disabled={busy}
              onChange={(e) => set("state", e.target.value)} style={{ marginTop: 4, width: "100%" }}>
              <option value="">—</option>
              {STATE_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {show("state")}
          </div>
          {input("email", "Email", { type: "email" })}
          {input("phone", "Mobile", { type: "tel", placeholder: "+1 555 010 0123" })}
        </div>
        <label style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 14, fontSize: 12, color: "#334155" }}>
          <input type="checkbox" data-testid="new-candidate-consent" checked={form.smsConsent} disabled={busy}
            onChange={(e) => set("smsConsent", e.target.checked)} style={{ marginTop: 2 }} />
          <span>The candidate agreed to receive text messages from ProTherapy Staffing. Leave unchecked unless they said so.</span>
        </label>
        {failure && <div data-testid="new-candidate-failure" style={{ ...errStyle, fontSize: 12, marginTop: 12 }}>{failure}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          <button className="nb" onClick={onClose} disabled={busy} style={{ background: "#f1f5f9", color: "#64748b", borderRadius: 8, padding: "9px 18px", fontSize: 13, fontWeight: 600 }}>Cancel</button>
          <button className="nb" data-testid="new-candidate-save" onClick={save} disabled={busy}
            style={{ background: "#1d4ed8", color: "white", borderRadius: 8, padding: "9px 18px", fontSize: 13, fontWeight: 600 }}>
            {busy ? "Creating…" : "Create candidate"}
          </button>
        </div>
      </div>
    </div>
  );
}
